import type { StaffMemoryFact } from "@lafayette/schemas";
import type { ReactNode } from "react";
import { Card, CardTitle } from "../../ui/card";
import { cn } from "../../ui/cn";
import { actionsFor, type FactAction, FactCard } from "../components/FactCard";
import { groupByKind, kindLabel } from "../labels";

// The three lists of facts of the workspace that show a fact with its own actions: the active facts (by
// kind), the disputed facts and the history. The review list has its own file (side by side).

type SectionProps = Readonly<{
  facts: readonly StaffMemoryFact[];
  customerId: string;
  now: Date;
  canManage: boolean;
  onAction: (action: FactAction, fact: StaffMemoryFact) => void;
}>;

const offered = (canManage: boolean, fact: StaffMemoryFact): readonly FactAction[] =>
  canManage ? actionsFor(fact) : [];

/** The frame of a section of facts: a titled card that the page's jump links can reach by its id. */
export function MemorySection({
  id,
  title,
  description,
  children,
  className,
}: Readonly<{
  id: string;
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
}>) {
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-4">
      <Card className={cn("grid gap-3.5 p-5", className)}>
        <div className="grid gap-0.5">
          <CardTitle id={`${id}-title`}>{title}</CardTitle>
          {description === undefined ? null : (
            <p className="m-0 text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {children}
      </Card>
    </section>
  );
}

export const EmptyNote = ({ children }: Readonly<{ children: ReactNode }>) => (
  <p className="m-0 text-sm text-muted-foreground">{children}</p>
);

export const FactList = ({ children }: Readonly<{ children: ReactNode }>) => (
  <ul className="m-0 grid list-none gap-3 p-0">{children}</ul>
);

export function ActiveSection({ facts, customerId, now, canManage, onAction }: SectionProps) {
  return (
    <MemorySection
      id="memory-active"
      className="gap-[18px]"
      title={`Active memories (${facts.length})`}
      description="What Lafayette uses when it plans for this customer, grouped by kind."
    >
      {facts.length === 0 ? (
        <EmptyNote>No active memories.</EmptyNote>
      ) : (
        groupByKind(facts).map((group) => (
          <section key={group.kind} className="grid gap-2.5" aria-label={kindLabel(group.kind)}>
            <h3 className="m-0 text-base leading-6 font-semibold">{kindLabel(group.kind)}</h3>
            <FactList>
              {group.facts.map((fact, index) => (
                <FactCard
                  key={fact.id}
                  fact={fact}
                  customerId={customerId}
                  now={now}
                  actions={offered(canManage, fact)}
                  onAction={onAction}
                  position={{ noun: "active memory", index: index + 1, total: group.facts.length }}
                />
              ))}
            </FactList>
          </section>
        ))
      )}
    </MemorySection>
  );
}

export function DisputedSection({ facts, customerId, now, canManage, onAction }: SectionProps) {
  return (
    <MemorySection
      id="memory-disputed"
      className="gap-[18px]"
      title={`Disputed memories (${facts.length})`}
      description="Someone questioned these. Lafayette does not use them until the dispute is resolved."
    >
      {facts.length === 0 ? (
        <EmptyNote>No disputed memories.</EmptyNote>
      ) : (
        <FactList>
          {facts.map((fact, index) => (
            <FactCard
              key={fact.id}
              fact={fact}
              customerId={customerId}
              now={now}
              showKind
              actions={offered(canManage, fact)}
              onAction={onAction}
              position={{ noun: "disputed memory", index: index + 1, total: facts.length }}
            />
          ))}
        </FactList>
      )}
    </MemorySection>
  );
}

export function HistorySection({ facts, customerId, now, canManage, onAction }: SectionProps) {
  return (
    <MemorySection
      id="memory-history"
      className="gap-[18px]"
      title={`History (${facts.length})`}
      description="Memories that were replaced, expired or deleted. A deleted memory keeps its history and has no value."
    >
      {facts.length === 0 ? (
        <EmptyNote>No closed memories.</EmptyNote>
      ) : (
        <FactList>
          {facts.map((fact, index) => (
            <FactCard
              key={fact.id}
              fact={fact}
              customerId={customerId}
              now={now}
              showKind
              actions={offered(canManage, fact)}
              onAction={onAction}
              position={{ noun: "closed memory", index: index + 1, total: facts.length }}
            />
          ))}
        </FactList>
      )}
    </MemorySection>
  );
}
